// ⚙️ Configuración del XML — Financiero / Configuración de Emisión → pestaña "Configuración del XML"
// Personaliza los campos <infoAdicional> del comprobante electrónico (SRI) por tipo de
// documento. Los 3 campos fijos (Observación / Proyecto / Centro de Costo) los agrega el
// sistema automáticamente; aquí se administran campos ADICIONALES (texto fijo y/o variables).
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, FileCode2, Info, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { Switch, fieldCls } from '@/features/inventario/shared';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

type TipoDoc = 'todos' | 'factura' | 'nota_credito' | 'retencion';
const TIPOS: { value: TipoDoc; label: string }[] = [
  { value: 'todos', label: 'Todos los comprobantes' },
  { value: 'factura', label: 'Factura' },
  { value: 'nota_credito', label: 'Nota de crédito' },
  { value: 'retencion', label: 'Retención' },
];

const FIJOS = ['Observación', 'Proyecto', 'Centro de Costo'];
const MAX_TOTAL = 15;
const MAX_PERSONALIZADOS = MAX_TOTAL - FIJOS.length; // 12

const VARIABLES: { token: string; label: string }[] = [
  { token: '{cliente.razon_social}', label: 'Razón social del cliente' },
  { token: '{cliente.identificacion}', label: 'RUC/cédula del cliente' },
  { token: '{cliente.email}', label: 'Email del cliente' },
  { token: '{cliente.direccion}', label: 'Dirección del cliente' },
  { token: '{cliente.telefono}', label: 'Teléfono del cliente' },
  { token: '{documento.numero}', label: 'Número del comprobante' },
  { token: '{documento.fecha}', label: 'Fecha de emisión' },
  { token: '{documento.total}', label: 'Total del comprobante' },
  { token: '{documento.subtotal}', label: 'Subtotal (sin impuestos)' },
  { token: '{documento.iva}', label: 'Valor del IVA' },
  { token: '{empresa.razon_social}', label: 'Razón social del emisor' },
  { token: '{empresa.ruc}', label: 'RUC del emisor' },
  { token: '{empresa.direccion}', label: 'Dirección del emisor' },
  { token: '{empresa.telefono}', label: 'Teléfono del emisor' },
  { token: '{empresa.email}', label: 'Email del emisor' },
];

interface FilaBD { id: string; tipo_doc: string; orden: number; nombre: string; valor: string; activo: boolean }
interface Fila { id: string | null; nombre: string; valor: string; activo: boolean }

let seq = 0;
const nuevaKey = () => `nueva-${Date.now()}-${seq++}`;

export function ConfiguracionXML() {
  const qc = useQueryClient();
  const [tipoDoc, setTipoDoc] = useState<TipoDoc>('todos');
  const [lineas, setLineas] = useState<(Fila & { key: string })[]>([]);
  const [focoKey, setFocoKey] = useState<string | null>(null);
  const inputsRef = useRef<Record<string, HTMLInputElement | null>>({});

  const { data, isLoading, isFetching } = useQuery<FilaBD[]>({
    queryKey: ['fe-info-adicional', tipoDoc],
    queryFn: async () => (await apiClient.post('/rpc/fe_info_adicional_get', { _tipo: tipoDoc }, FIN)).data || [],
  });

  useEffect(() => {
    const filas = (data || [])
      .slice()
      .sort((a, b) => a.orden - b.orden)
      .map((r) => ({ key: r.id || nuevaKey(), id: r.id, nombre: r.nombre, valor: r.valor, activo: r.activo }));
    setLineas(filas);
  }, [data]);

  const guardar = useMutation({
    mutationFn: async () => {
      const payload = lineas.map((l, i) => ({ nombre: l.nombre.trim(), valor: l.valor, activo: l.activo, orden: i + 1 }));
      return (await apiClient.post('/rpc/fe_info_adicional_set', { _tipo: tipoDoc, _lineas: payload }, FIN)).data;
    },
    onSuccess: () => { toast.success('Configuración del XML guardada'); void qc.invalidateQueries({ queryKey: ['fe-info-adicional'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo guardar'),
  });

  const agregar = () => {
    if (lineas.length >= MAX_PERSONALIZADOS) return;
    const key = nuevaKey();
    setLineas((ls) => [...ls, { key, id: null, nombre: '', valor: '', activo: true }]);
    setFocoKey(key);
  };
  const eliminar = (key: string) => setLineas((ls) => ls.filter((l) => l.key !== key));
  const mover = (key: string, dir: -1 | 1) => setLineas((ls) => {
    const idx = ls.findIndex((l) => l.key === key);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= ls.length) return ls;
    const copia = ls.slice();
    [copia[idx], copia[j]] = [copia[j], copia[idx]];
    return copia;
  });
  const patch = (key: string, cambios: Partial<Fila>) => setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...cambios } : l)));

  const insertarVariable = (token: string) => {
    const key = focoKey || lineas[lineas.length - 1]?.key;
    if (!key) { toast.error('Agrega un campo primero'); return; }
    const el = inputsRef.current[key];
    setLineas((ls) => ls.map((l) => {
      if (l.key !== key) return l;
      const pos = el?.selectionStart ?? l.valor.length;
      return { ...l, valor: l.valor.slice(0, pos) + token + l.valor.slice(pos) };
    }));
    requestAnimationFrame(() => {
      const elAfter = inputsRef.current[key];
      if (elAfter) {
        const pos = (elAfter.selectionStart ?? elAfter.value.length) + token.length;
        elAfter.focus();
        try { elAfter.setSelectionRange(pos, pos); } catch { /* no-op */ }
      }
    });
  };

  const totalCampos = lineas.length + FIJOS.length;
  const tope = lineas.length >= MAX_PERSONALIZADOS;

  return (
    <div className="grid gap-6 lg:grid-cols-3 max-w-6xl">
      <div className="lg:col-span-2 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><FileCode2 className="w-4 h-4" /> Campos de información adicional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium" htmlFor="xml-tipo">Tipo de comprobante</label>
              <select id="xml-tipo" className={`${fieldCls} w-auto`} value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value as TipoDoc)}>
                {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {isFetching && <span className="text-xs text-muted-foreground">Cargando…</span>}
            </div>

            <p className="text-[11px] text-muted-foreground">
              El <strong>Valor</strong> admite texto fijo y/o variables (ver panel a la derecha); se reemplazan al emitir cada comprobante.
              Los 3 campos fijos — <strong>Observación</strong>, <strong>Proyecto</strong> y <strong>Centro de Costo</strong> — ya viajan siempre en el XML y se mantienen automáticamente (no se muestran aquí).
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-1 px-2 w-48">Nombre</th>
                    <th className="py-1 px-2">Valor</th>
                    <th className="py-1 px-2 w-16 text-center">Activo</th>
                    <th className="py-1 px-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.length === 0 && (
                    <tr><td colSpan={4} className="py-3 px-2 text-xs text-muted-foreground">Sin campos personalizados para este tipo de comprobante. Usa «+ Agregar campo».</td></tr>
                  )}
                  {lineas.map((l, i) => (
                    <tr key={l.key} className="border-b last:border-0">
                      <td className="py-1 px-2">
                        <input className={fieldCls} placeholder="Ej. Vendedor" value={l.nombre} maxLength={300}
                          onChange={(e) => patch(l.key, { nombre: e.target.value })} />
                      </td>
                      <td className="py-1 px-2">
                        <input
                          ref={(el) => { inputsRef.current[l.key] = el; }}
                          className={fieldCls} placeholder="Texto fijo y/o {variable}" value={l.valor}
                          onFocus={() => setFocoKey(l.key)}
                          onChange={(e) => patch(l.key, { valor: e.target.value })}
                        />
                      </td>
                      <td className="py-1 px-2 text-center">
                        <Switch checked={l.activo} onChange={(v) => patch(l.key, { activo: v })} label="" />
                      </td>
                      <td className="py-1 px-2 text-right whitespace-nowrap">
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted disabled:opacity-30" title="Subir" disabled={i === 0} onClick={() => mover(l.key, -1)}><ArrowUp className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted disabled:opacity-30" title="Bajar" disabled={i === lineas.length - 1} onClick={() => mover(l.key, 1)}><ArrowDown className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Eliminar" onClick={() => eliminar(l.key)}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-2">
              <Button size="sm" variant="outline" disabled={tope} onClick={agregar}>
                <Plus className="w-4 h-4 mr-1" /> Agregar campo
              </Button>
              <span className="text-[11px] text-muted-foreground">{totalCampos} de {MAX_TOTAL} campos (incluye los 3 fijos)</span>
            </div>

            {tope && <p className="text-[11px] text-amber-600">Llegaste al máximo de campos personalizados permitidos para este tipo de comprobante (12 + 3 fijos = 15).</p>}

            <div className="flex justify-end pt-1">
              <Button isLoading={guardar.isPending} disabled={isLoading} onClick={() => {
                if (lineas.some((l) => !l.nombre.trim())) { toast.error('Cada campo necesita un nombre'); return; }
                guardar.mutate();
              }}>
                {!guardar.isPending && <Save className="w-4 h-4" />} Guardar configuración
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Info className="w-4 h-4" /> Variables disponibles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-[11px] text-muted-foreground">Haz clic sobre una variable para insertarla en el campo <strong>Valor</strong> seleccionado.</p>
            <div className="flex flex-wrap gap-1.5">
              {VARIABLES.map((v) => (
                <button
                  key={v.token}
                  type="button"
                  title={v.label}
                  onClick={() => insertarVariable(v.token)}
                  className="rounded-md border border-input bg-muted/40 px-2 py-1 text-[11px] font-mono hover:bg-muted"
                >
                  {v.token}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-900/10">
          <CardContent className="p-3 text-[11px] text-muted-foreground">
            El SRI admite <strong>máximo 15 campos</strong> de información adicional por comprobante, contando los 3 fijos (Observación, Proyecto, Centro de Costo).
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
